import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { useReportMenuItems, useRtl } from "@ryde/redux";
import {
  DashboardCardNameEnum,
  IReportApiDto,
  IReportCriteriaFilters,
  IReportCriteriaFiltersSchemaValues,
  IReportTemplateFilters,
  isOperatorReportFilterType,
  ReportPeriodGroupingEnum,
  ReportsApi,
  topCalendarDatesAtomFamily,
  trimSeconds,
} from "@ryde/shared";

import { ANALYTIC_REPORT_LABEL_KEYS } from "../../dynamic-dashboard.analytic-reports";
import { dashboardCardsAtom } from "../../dynamic-dashboard.atoms";
import {
  DASHBOARD_ENTITY,
  DASHBOARD_MAX_CARDS,
  DashboardChartType,
  IDashboardCard,
  TDashboardCardSize,
  TDashboardCardSource,
} from "../../dynamic-dashboard.models";
import {
  DASHBOARD_SYSTEM_CARDS,
  SYSTEM_CARD_FILTER_SOURCES,
} from "../../dynamic-dashboard.system-cards.constants";
import {
  buildSystemCard,
  getSystemCardDefinition,
} from "../../dynamic-dashboard.system-cards.utils";
import { IUseDashboardCardEditor } from "./dashboard-card-editor.models";
import {
  buildCardDraftSignature,
  deriveGroupingForDims,
  getReportChartOptions,
  removeOrClearCardFilter,
} from "./dashboard-card-editor.utils";

/**
 * All the state + effects behind the card editor: the chartable-report list,
 * the report fetch (with a regroup pass for grouping-expected reports), the
 * derived Group-by / Metric / Series options with their auto-defaults, the
 * auto-name, and Save (writes the card into `dashboardCardsAtom`, then closes).
 * Kept out of the component so the fields view is pure presentation.
 */
export const useDashboardCardEditor = (
  cardId: string | null,
  onClose: () => void,
): IUseDashboardCardEditor => {
  const { t } = useTranslation();
  const [cards, setCards] = useRecoilState(dashboardCardsAtom);
  const { reportMenuItems } = useReportMenuItems();
  const calendarDates = useRecoilValue(
    topCalendarDatesAtomFamily(DASHBOARD_ENTITY),
  );
  const { isRtl } = useRtl();

  const editingCard = cardId
    ? cards.find(card => card.id === cardId)
    : undefined;

  const chartableReports = useMemo(
    () =>
      (reportMenuItems ?? [])
        .filter(group => ANALYTIC_REPORT_LABEL_KEYS[group.reportTemplateId])
        .map(group => ({
          id: group.reportItems[0]?.id,
          labelKey: ANALYTIC_REPORT_LABEL_KEYS[group.reportTemplateId],
        }))
        .filter(
          (report): report is { id: number; labelKey: string } =>
            typeof report.id === "number",
        ),
    [reportMenuItems],
  );

  const [reportId, setReportId] = useState<number | "">(
    editingCard?.reportId ?? "",
  );
  const [reportDto, setReportDto] = useState<IReportApiDto | undefined>(
    undefined,
  );
  const [loadingReport, setLoadingReport] = useState(false);
  const [dimensionKey, setDimensionKey] = useState(
    editingCard?.dimensionKey ?? "",
  );
  const [metricKey, setMetricKey] = useState(editingCard?.metricKey ?? "");
  const [seriesKey, setSeriesKey] = useState(editingCard?.seriesKey ?? "");
  const [chartType, setChartType] = useState<DashboardChartType>(
    editingCard && editingCard.chartType !== DashboardChartType.Legacy
      ? editingCard.chartType
      : DashboardChartType.VerticalBar,
  );
  const [size, setSize] = useState<TDashboardCardSize>(
    editingCard?.size ?? "narrow",
  );
  // A card is either a chart over an analytic report, or one of the system cards
  // fed by the dashboard aggregation endpoint. Editing an existing Legacy card
  // opens on the system tab so it stays what it is unless the user switches.
  const [cardSource, setCardSource] = useState<TDashboardCardSource>(
    editingCard?.legacyCardName ? "system" : "report",
  );
  const [systemCardName, setSystemCardName] = useState<string>(
    editingCard?.legacyCardName ?? "",
  );
  const [cardFilters, setCardFilters] =
    useState<IReportCriteriaFiltersSchemaValues>(
      editingCard?.cardFilters ?? {},
    );
  // Legacy/default cards store an English `title` but render a localized header
  // via t(`dashboard.<CARD>.TITLE`); seed the editor with that same localized
  // name so editing in Hebrew doesn't show the English default.
  const editingCardName = editingCard?.legacyCardName
    ? t(`dashboard.${editingCard.legacyCardName}.TITLE`)
    : (editingCard?.title ?? "");
  const [name, setName] = useState(editingCardName);
  const [nameEdited, setNameEdited] = useState(Boolean(editingCard));

  const { dimensionOptions, metricOptions, flexGroupingTemplateKeys } = useMemo(
    () => getReportChartOptions(reportDto, t),
    [reportDto, t],
  );

  const isMultiSeries =
    chartType === DashboardChartType.Clustered ||
    chartType === DashboardChartType.Stacked;
  const seriesOptions = useMemo(
    () => dimensionOptions.filter(option => option.value !== dimensionKey),
    [dimensionOptions, dimensionKey],
  );

  /**
   * Every filter the selected report declares — narrower and more correct than
   * the dashboard-wide bar's union, since only these keys mean anything to this
   * report. A report that offers few filters here (bookings offers 4, rides 20)
   * is a report-template limitation, not a dashboard one: the engine only applies
   * the keys the template lists in `filters`.
   *
   * Operator-typed filters (number comparison/between, RYDE-8067) are excluded:
   * their values are criteria.where descriptors, and the dashboard card fetch
   * (buildUserFilters) only speaks the legacy { value } shape. Report-screen-only
   * until the card fetch decodes them.
   */
  const filterSchema = useMemo((): IReportTemplateFilters => {
    const available = reportDto?.reportTemplate?.availableFilters ?? {};
    return Object.fromEntries(
      Object.entries(available).filter(
        ([, settings]) =>
          !isOperatorReportFilterType(settings?.filterFieldTypeId),
      ),
    );
  }, [reportDto?.reportTemplate?.availableFilters]);

  const setCardFilterValues = useEvent(
    ({
      templateKey,
      values,
    }: {
      templateKey: string;
      values: number[] | string[];
    }): void => setCardFilters(prev => ({ ...prev, [templateKey]: values })),
  );

  const removeCardFilter = useEvent(
    ({ templateKey }: { templateKey: string }): void =>
      setCardFilters(prev =>
        removeOrClearCardFilter({ filters: prev, templateKey }),
      ),
  );

  const addCardFilter = useEvent(
    ({ templateKey }: { templateKey?: string }): void => {
      if (!templateKey) {
        return;
      }
      // `null` is the shared "filter present, no value chosen" marker.
      setCardFilters(prev =>
        templateKey in prev ? prev : { ...prev, [templateKey]: null },
      );
    },
  );

  const requestIdRef = useRef(0);

  const runReportFetch = useEvent(
    async (grouping: {
      groupingParams: string[];
      periodGrouping?: ReportPeriodGroupingEnum;
    }): Promise<void> => {
      if (typeof reportId !== "number") {
        setReportDto(undefined);
        return;
      }
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setLoadingReport(true);
      try {
        const filters: IReportCriteriaFilters = {};
        if (calendarDates) {
          filters.dates = {
            value: [
              trimSeconds(calendarDates.date1),
              trimSeconds(calendarDates.date2),
            ],
          };
        }
        const res = await ReportsApi.fetchReportData({
          id: reportId,
          filters,
          flexGrouping: {
            groupingParams: grouping.groupingParams,
            periodGrouping: grouping.periodGrouping,
          },
        });
        if (requestId === requestIdRef.current) {
          setReportDto(res?.success ? res.data : undefined);
        }
      } catch {
        if (requestId === requestIdRef.current) {
          setReportDto(undefined);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingReport(false);
        }
      }
    },
  );

  // Fetch the selected report (no grouping yet) to read its columns/template.
  useEffect(() => {
    if (typeof reportId !== "number") {
      setReportDto(undefined);
      return;
    }
    runReportFetch({ groupingParams: [] });
  }, [reportId, runReportFetch]);

  // Analytics reports that "expect grouping params" return no metric columns
  // until a grouping is applied. Once a Group-by dimension is chosen, refetch
  // with it so the real columns (and the Metric options) come back.
  useEffect(() => {
    if (!reportDto?.groupingParamsExpected || !dimensionKey) {
      return;
    }
    runReportFetch(
      deriveGroupingForDims([dimensionKey], flexGroupingTemplateKeys),
    );
  }, [
    reportDto?.groupingParamsExpected,
    dimensionKey,
    flexGroupingTemplateKeys,
    runReportFetch,
  ]);

  // Default the dimension/metric to the first available option once they load.
  useEffect(() => {
    if (
      dimensionOptions.length &&
      !dimensionOptions.some(option => option.value === dimensionKey)
    ) {
      setDimensionKey(dimensionOptions[0].value);
    }
  }, [dimensionOptions, dimensionKey]);

  useEffect(() => {
    if (
      metricOptions.length &&
      !metricOptions.some(option => option.value === metricKey)
    ) {
      setMetricKey(metricOptions[0].value);
    }
  }, [metricOptions, metricKey]);

  // Default the series dimension (clustered/stacked) to the first option that
  // isn't the category dimension.
  useEffect(() => {
    if (!isMultiSeries) {
      return;
    }
    if (
      seriesOptions.length &&
      !seriesOptions.some(option => option.value === seriesKey)
    ) {
      setSeriesKey(seriesOptions[0].value);
    }
  }, [isMultiSeries, seriesOptions, seriesKey]);

  // Switching the report can strand filters the new one doesn't declare. The
  // engine would ignore them anyway, but they'd linger in the panel — so drop
  // them once the new report's schema is in (never while it's still loading,
  // which would wipe the filters of the card being edited).
  useEffect(() => {
    if (!reportDto) {
      return;
    }
    setCardFilters(prev => {
      const kept = Object.entries(prev).filter(([key]) => key in filterSchema);
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
  }, [reportDto, filterSchema]);

  const dimensionLabel =
    dimensionOptions.find(option => option.value === dimensionKey)?.label ?? "";
  const metricLabel =
    metricOptions.find(option => option.value === metricKey)?.label ?? "";

  // Auto-name is "{Metric}/{Dimension}". In RTL (Hebrew) the parts are ordered
  // dimension-first so the chosen dimension (right-hand "Group by" list) reads
  // on the right and the metric on the left — matching the panel's layout.
  useEffect(() => {
    if (nameEdited || !metricLabel) {
      return;
    }

    // A number card has no user-chosen dimension, so naming it after one would be
    // misleading — it is just the metric.
    if (chartType === DashboardChartType.Stat) {
      setName(metricLabel);
      return;
    }

    if (dimensionLabel) {
      setName(
        isRtl
          ? `${dimensionLabel}/${metricLabel}`
          : `${metricLabel}/${dimensionLabel}`,
      );
    }
  }, [metricLabel, dimensionLabel, nameEdited, isRtl, chartType]);

  const systemCardOptions = useMemo(
    () =>
      DASHBOARD_SYSTEM_CARDS.map(definition => ({
        value: definition.cardName as string,
        label: t(`dashboard.${definition.cardName}.TITLE`),
      })),
    [t],
  );

  const isSystemSource = cardSource === "system";

  /**
   * Filter widget config for the selected system card — only the keys its entity
   * supports, so the panel never offers a filter the backend would ignore. Shaped
   * like a report's `availableFilters` so the same widgets render it.
   */
  const systemFilterSchema = useMemo((): IReportTemplateFilters => {
    const definition = getSystemCardDefinition(
      systemCardName as DashboardCardNameEnum,
    );
    const schema: IReportTemplateFilters = {};

    definition?.supportedFilters?.forEach(key => {
      schema[key] = { dropdownSource: SYSTEM_CARD_FILTER_SOURCES[key] };
    });

    return schema;
  }, [systemCardName]);

  // Switching the selected card strands filters the new one doesn't support.
  useEffect(() => {
    if (!isSystemSource) {
      return;
    }
    setCardFilters(prev => {
      const kept = Object.entries(prev).filter(
        ([key]) => key in systemFilterSchema,
      );
      return kept.length === Object.keys(prev).length
        ? prev
        : Object.fromEntries(kept);
    });
  }, [isSystemSource, systemFilterSchema]);

  const isSaveDisabled = isSystemSource
    ? !systemCardName
    : typeof reportId !== "number" ||
      !dimensionKey ||
      !metricKey ||
      (isMultiSeries && !seriesKey) ||
      name.trim().length === 0;

  // Compared against the signature taken when the panel opened, so closing can
  // warn about edits that were never saved.
  const draftSignature = buildCardDraftSignature({
    cardSource,
    systemCardName,
    reportId,
    name,
    chartType,
    size,
    dimensionKey,
    metricKey,
    seriesKey,
    cardFilters,
  });
  const openedSignatureRef = useRef(draftSignature);
  const isDirty = draftSignature !== openedSignatureRef.current;

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value);
    setNameEdited(true);
  };

  /** Replace the edited card, or append a new one while under the cap. */
  const commitCard = (card: IDashboardCard): void => {
    if (cardId && editingCard) {
      setCards(prev => prev.map(item => (item.id === cardId ? card : item)));
    } else {
      setCards(prev =>
        prev.length >= DASHBOARD_MAX_CARDS ? prev : [...prev, card],
      );
    }
    onClose();
  };

  const handleSaveSystemCard = (): void => {
    const definition = DASHBOARD_SYSTEM_CARDS.find(
      item => item.cardName === systemCardName,
    );

    if (!definition) {
      return;
    }

    commitCard(
      buildSystemCard({
        definition,
        // Keep the card's identity when editing so the grid doesn't remount it.
        id: cardId ?? `card-${Date.now()}`,
        size,
        // No title: the card header renders `dashboard.<CARD>.TITLE`, so the
        // definition's English name is only ever a fallback.
        cardFilters,
      }),
    );
  };

  const handleSave = (): void => {
    if (isSaveDisabled) {
      return;
    }

    if (isSystemSource) {
      handleSaveSystemCard();
      return;
    }

    if (typeof reportId !== "number") {
      return;
    }
    const dims =
      isMultiSeries && seriesKey ? [dimensionKey, seriesKey] : [dimensionKey];
    const { groupingParams, periodGrouping } = deriveGroupingForDims(
      dims,
      flexGroupingTemplateKeys,
    );
    const fields = {
      title: name.trim(),
      subtitle: dimensionLabel,
      chartType,
      size,
      reportId,
      dimensionKey,
      seriesKey: isMultiSeries ? seriesKey : undefined,
      metricKey,
      groupingParams,
      periodGrouping,
      cardFilters,
      dimensionLabel,
      metricLabel,
      linkTo: "/reports",
      // Editing a card makes it a custom chart; drop any legacy link. `linkFilters`
      // goes with it — those name controls on the old target grid, which mean
      // nothing on /reports. `singleValueFilterKeys` is kept: it describes the
      // card's own filters, which a report card can have too.
      legacyCardName: undefined,
      linkFilters: undefined,
    };
    if (cardId && editingCard) {
      setCards(prev =>
        prev.map(card => (card.id === cardId ? { ...card, ...fields } : card)),
      );
    } else {
      setCards(prev =>
        prev.length >= DASHBOARD_MAX_CARDS
          ? prev
          : [...prev, { id: `card-${Date.now()}`, ...fields }],
      );
    }
    onClose();
  };

  return {
    cardSource,
    setCardSource,
    systemCardOptions,
    systemCardName,
    setSystemCardName,
    systemFilterSchema,
    chartableReports,
    reportId,
    setReportId,
    name,
    handleNameChange,
    chartType,
    setChartType,
    size,
    setSize,
    filterSchema,
    cardFilters,
    singleValueFilterKeys: editingCard?.singleValueFilterKeys,
    addCardFilter,
    setCardFilterValues,
    removeCardFilter,
    loadingReport,
    groupingParamsExpected: Boolean(reportDto?.groupingParamsExpected),
    dimensionOptions,
    metricOptions,
    seriesOptions,
    dimensionKey,
    setDimensionKey,
    metricKey,
    setMetricKey,
    seriesKey,
    setSeriesKey,
    isMultiSeries,
    isSaveDisabled,
    isDirty,
    handleSave,
  };
};
